import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Table,
  Alert,
  Container,
  Card,
  Button,
  Badge,
  Row,
  Col
} from "react-bootstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "../components/HusariaHeader";
import { useNavigate } from "react-router-dom";

const ActiveCalls = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const [calls, setCalls] = useState([]);
  const [error, setError] = useState(null);
  const [directionFilter, setDirectionFilter] = useState("all");

  const fetchActiveCalls = async () => {
    try {
      const site = JSON.parse(localStorage.getItem("selectedSite"));
      const response = await fetch(
        `${serverHost}/active_calls/${site.site_uuid}`,
      );
      if (!response.ok) throw new Error("Failed to fetch active calls");
      const data = await response.json();
      setCalls(data.result);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchActiveCalls();
    const interval = setInterval(fetchActiveCalls, 5000);
    return () => clearInterval(interval);
  }, []);

  const filteredCalls = calls.filter((call) => {
    if (directionFilter === "all") return true;
    return call.direction?.toLowerCase() === directionFilter;
  });

  const activeCallsCount = calls.length;
  const inboundCount = calls.filter(
    (c) => c.direction?.toLowerCase() === "inbound",
  ).length;
  const outboundCount = calls.filter(
    (c) => c.direction?.toLowerCase() === "outbound",
  ).length;
  const localCount = calls.filter(
    (c) => c.direction?.toLowerCase() === "local",
  ).length;

  const formatDuration = (seconds) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const hangup = async (call, suppressRefresh = false) => {
    try {
      const resp = await fetch(`${serverHost}/hangup/${call}`);
      if (!resp.ok) throw new Error("Failed to hang up call " + call);
      if (!suppressRefresh) fetchActiveCalls();
    } catch (err) {
      console.log("Failed to hang up ", err);
      setError("Failed to hangup call: " + err);
    }
  };

  const hangupAll = async () => {
    await Promise.all(calls.map((call) => hangup(call.uuid, true)));
    fetchActiveCalls();
  };

  const getDirectionBadge = (direction) => {
    const dirLower = direction?.toLowerCase();
    if (dirLower === "inbound") return <Badge bg="success">Inbound</Badge>;
    if (dirLower === "outbound") return <Badge bg="primary">Outbound</Badge>;
    if (dirLower === "local") return <Badge bg="info">Local</Badge>;
    if (dirLower === "emergency") return <Badge bg="warning">Emergency</Badge>;
    return <Badge bg="secondary">{direction || "Unknown"}</Badge>;
  };

  const info_style = {
    backgroundColor: isDark ? "#1f2937" : "#fff",
    borderRadius: "1rem",
    overflow: "hidden",
  };

  const cardHeaderStyle = {
    backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
    color: isDark ? "#e2e8f0" : "#111",
  };

  return (
    <>
      <HusariaHeader name="Active Calls" parentName="HUCS" padding="9.5rem" />

      <Container fluid className="mt--9">
        {/* Statistics Cards */}
        <Row className="mb-4">
          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Active Calls
                    </p>
                    <h2 className="mb-0" style={{ fontWeight: 700 }}>
                      {activeCallsCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                    <i className="fas fa-phone fa-rotate-90"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Inbound
                    </p>
                    <h2
                      className="mb-0"
                      style={{ fontWeight: 700, color: "#28a745" }}
                    >
                      {inboundCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                    <i className="fas fa-sign-in-alt"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Outbound
                    </p>
                    <h2
                      className="mb-0"
                      style={{ fontWeight: 700, color: "#007bff" }}
                    >
                      {outboundCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                    <i className="fas fa-sign-out-alt"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg="3" md="6" className="mb-3">
            <Card
              className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
              style={info_style}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <p
                      className="mb-0 text-muted"
                      style={{ fontSize: "0.875rem" }}
                    >
                      Local
                    </p>
                    <h2
                      className="mb-0"
                      style={{ fontWeight: 700, color: "#17a2b8" }}
                    >
                      {localCount}
                    </h2>
                  </div>
                  <div style={{ fontSize: "2.5rem", opacity: 0.9 }}>
                    <i className="fas fa-map-pin"></i>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Active Calls Table */}
        <Card
          className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
        >
          <Card.Header
            className="d-flex justify-content-between align-items-center"
            style={cardHeaderStyle}
          >
            <div className="d-flex gap-2">
              <Button
                variant={
                  directionFilter === "all" ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setDirectionFilter("all")}
              >
                All ({activeCallsCount})
              </Button>
              <Button
                variant={
                  directionFilter === "inbound" ? "success" : "outline-success"
                }
                size="sm"
                onClick={() => setDirectionFilter("inbound")}
              >
                Inbound ({inboundCount})
              </Button>
              <Button
                variant={
                  directionFilter === "outbound" ? "primary" : "outline-primary"
                }
                size="sm"
                onClick={() => setDirectionFilter("outbound")}
              >
                Outbound ({outboundCount})
              </Button>
              <Button
                variant={directionFilter === "local" ? "info" : "outline-info"}
                size="sm"
                onClick={() => setDirectionFilter("local")}
              >
                Local ({localCount})
              </Button>
              <Button variant="info" size="sm" onClick={fetchActiveCalls}>
                Refresh
              </Button>
            </div>
          </Card.Header>

          <Card.Body
            style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}
          >
            {error ? (
              <Alert variant="danger" className="m-3">
                {error}
              </Alert>
            ) : filteredCalls.length === 0 ? (
              <div
                className="text-center py-5"
                style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
              >
                <h5>No active calls</h5>
                <p className="mb-0">
                  There are currently no{" "}
                  {directionFilter !== "all" ? directionFilter : ""} calls in
                  progress.
                </p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table
                  striped
                  hover
                  responsive
                  bordered
                  className="mb-0"
                  style={{
                    minWidth: "1000px",
                    backgroundColor: isDark ? "#1f2937" : "#fff",
                    color: isDark ? "#e2e8f0" : "#212529",
                  }}
                >
                  <thead
                    style={{
                      backgroundColor: isDark ? "#1f2937" : "#fff",
                      fontWeight: "bold",
                      color: isDark ? "#e2e8f0" : "#212529",
                    }}
                  >
                    <tr>
                      <th className="text-center">Profile</th>
                      <th className="text-center">Call ID</th>
                      <th className="text-center">Direction</th>
                      <th className="text-center">Caller</th>
                      <th className="text-center">Destination</th>
                      <th className="text-center">Duration</th>
                      <th className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCalls.map((call) => (
                      <tr
                        key={call.uuid}
                        style={{ color: isDark ? "#e2e8f0" : "#212529" }}
                      >
                        <td
                          className="text-center"
                          style={{ fontFamily: "monospace" }}
                        >
                          {call.profile || "-"}
                        </td>
                        <td
                          className="text-center"
                          style={{ fontFamily: "monospace" }}
                        >
                          {call.call_id || "-"}
                        </td>
                        <td className="text-center">
                          {getDirectionBadge(call.direction)}
                        </td>
                        <td className="text-center">
                          {call.caller_number || "-"}
                        </td>
                        <td className="text-center">
                          {call.callee_number || "-"}
                        </td>
                        <td className="text-center">
                          {formatDuration(call.duration)}
                        </td>
                        <td className="text-center">
                          <Badge bg="warning" text="dark">
                            {call.status || "Active"}
                          </Badge>
                        </td>
                        <td className="text-center">
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => hangup(call.uuid)}
                          >
                            Hang Up
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ActiveCalls;
